// CSDIntegrationPage.js — Per-country CSD integration settings for frontier markets
import React, { useState } from 'react';
import { Database, CheckCircle, AlertTriangle, Wifi } from 'lucide-react';
import { NATIONALITY_PRIORITY } from '../../../data/countries';

// Country metadata: code, currency
const COUNTRY_META = {
  Kenya:        { code: 'KE', currency: 'KES' },
  India:        { code: 'IN', currency: 'INR' },
  Brazil:       { code: 'BR', currency: 'BRL' },
  Mexico:       { code: 'MX', currency: 'MXN' },
  Turkey:       { code: 'TR', currency: 'TRY' },
  Bangladesh:   { code: 'BD', currency: 'BDT' },
  'South Africa': { code: 'ZA', currency: 'ZAR' },
  Egypt:        { code: 'EG', currency: 'EGP' },
  Ethiopia:     { code: 'ET', currency: 'ETB' },
};

// Default CSD configs per country — Kenya pre-populated from csdGateway
const buildDefaults = () => {
  const configs = {};
  NATIONALITY_PRIORITY.forEach(name => {
    const meta = COUNTRY_META[name] || { code: '--', currency: '---' };
    const isKenya = name === 'Kenya';
    configs[name] = {
      provider:          isKenya ? 'DhowCSD' : '',
      countryCode:       meta.code,
      currency:          meta.currency,
      endpointUrl:       isKenya ? 'https://api.dhowcsd.co.ke/v1' : '',
      apiKey:            isKenya ? '••••••••••••••••dk4R' : '',
      authMethod:        isKenya ? 'api_key' : 'api_key',
      settlementAccount: isKenya ? 'CSD-KE-SETTLE-001' : '',
      cutoffTime:        isKenya ? '14:00' : '',
      nettingEnabled:    isKenya,
      status:            isKenya ? 'active' : 'disabled',
      lastTestResult:    isKenya ? 'Pass — 120 ms (2025-01-14)' : null,
    };
  });
  return configs;
};

const CSDIntegrationPage = () => {
  const [selectedCountry, setSelectedCountry] = useState(NATIONALITY_PRIORITY[0]);
  const [configs, setConfigs] = useState(buildDefaults);
  const [testing, setTesting] = useState(false);

  const cfg = configs[selectedCountry];
  const meta = COUNTRY_META[selectedCountry] || { code: '--', currency: '---' };

  const update = (field, value) => {
    setConfigs(prev => ({
      ...prev,
      [selectedCountry]: { ...prev[selectedCountry], [field]: value },
    }));
  };

  const handleTestConnection = () => {
    setTesting(true);
    setTimeout(() => {
      update('lastTestResult', cfg.endpointUrl
        ? `Pass — ${Math.floor(80 + Math.random() * 200)} ms (${new Date().toISOString().split('T')[0]})`
        : 'Fail — No endpoint configured');
      setTesting(false);
    }, 1200);
  };

  return (
    <div className="space-y-8 font-sans text-black antialiased">
      {/* Header */}
      <div className="border-b border-zinc-200 pb-6">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">Integrations</span>
        <h1 className="font-sans text-3xl font-bold text-black">CSD Integration</h1>
        <p className="font-sans text-xs text-zinc-600 mt-1">Configure Central Securities Depository connections per frontier market</p>
      </div>

      {/* Country pills */}
      <div className="flex flex-wrap gap-2">
        {NATIONALITY_PRIORITY.map(name => {
          const m = COUNTRY_META[name] || {};
          const active = name === selectedCountry;
          return (
            <button
              key={name}
              onClick={() => setSelectedCountry(name)}
              className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] border transition-all rounded-none ${
                active
                  ? 'bg-[#4D4D4D] text-white border-[#4D4D4D]'
                  : 'bg-white text-zinc-700 border-zinc-300 hover:border-[#4D4D4D]'
              }`}
            >
              {m.code} — {name}
            </button>
          );
        })}
      </div>

      {/* Config cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CSD Provider */}
        <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
          <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
            <Database className="w-4 h-4 text-zinc-600" />
            <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">CSD Provider</h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Provider Name</label>
              <input
                type="text"
                value={cfg.provider}
                onChange={e => update('provider', e.target.value)}
                placeholder="e.g. DhowCSD, CDSL, B3"
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Country Code</label>
                <input
                  type="text"
                  value={meta.code}
                  readOnly
                  className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-500 bg-zinc-50"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Currency</label>
                <input
                  type="text"
                  value={meta.currency}
                  readOnly
                  className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-500 bg-zinc-50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* API Configuration */}
        <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
          <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
            <Wifi className="w-4 h-4 text-zinc-600" />
            <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">API Configuration</h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Endpoint URL</label>
              <input
                type="text"
                value={cfg.endpointUrl}
                onChange={e => update('endpointUrl', e.target.value)}
                placeholder="https://api.example.com/v1"
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">API Key</label>
              <input
                type="password"
                value={cfg.apiKey}
                onChange={e => update('apiKey', e.target.value)}
                placeholder="Enter API key"
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Auth Method</label>
              <select
                value={cfg.authMethod}
                onChange={e => update('authMethod', e.target.value)}
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
              >
                <option value="api_key">API Key</option>
                <option value="oauth2">OAuth 2.0</option>
                <option value="certificate">Client Certificate</option>
              </select>
            </div>
          </div>
        </div>

        {/* Settlement Configuration */}
        <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
          <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
            <Database className="w-4 h-4 text-zinc-600" />
            <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Settlement Configuration</h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Settlement Account ID</label>
              <input
                type="text"
                value={cfg.settlementAccount}
                onChange={e => update('settlementAccount', e.target.value)}
                placeholder="CSD-XX-SETTLE-001"
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-1">Cut-off Time</label>
              <input
                type="time"
                value={cfg.cutoffTime}
                onChange={e => update('cutoffTime', e.target.value)}
                className="w-full px-3 py-2 border border-zinc-200 rounded-none font-mono text-xs text-zinc-900 bg-white"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-xs font-medium text-zinc-900">Netting</p>
                <p className="text-[10px] font-mono text-zinc-500">Enable settlement netting for this market</p>
              </div>
              <button
                onClick={() => update('nettingEnabled', !cfg.nettingEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cfg.nettingEnabled ? 'bg-black' : 'bg-zinc-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cfg.nettingEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Status & Testing */}
        <div className="bg-white rounded-none border border-zinc-200 overflow-hidden">
          <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
            <CheckCircle className="w-4 h-4 text-zinc-600" />
            <h3 className="text-xs font-mono font-semibold text-zinc-900 uppercase tracking-wider">Status & Testing</h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-[10px] font-mono font-medium text-zinc-500 uppercase tracking-wider mb-2">Status</label>
              <div className="flex gap-3">
                {['active', 'testing', 'disabled'].map(s => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`status-${selectedCountry}`}
                      checked={cfg.status === s}
                      onChange={() => update('status', s)}
                      className="accent-black"
                    />
                    <span className="font-mono text-xs uppercase tracking-wider text-zinc-700">{s}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="pt-2">
              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="flex items-center gap-2 px-4 py-2 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-wider hover:bg-black transition-colors rounded-none disabled:opacity-50"
              >
                {testing ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Wifi className="w-3.5 h-3.5" />
                )}
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
            {cfg.lastTestResult && (
              <div className={`p-3 border rounded-none font-mono text-xs ${
                cfg.lastTestResult.startsWith('Pass')
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className="flex items-center gap-2">
                  {cfg.lastTestResult.startsWith('Pass')
                    ? <CheckCircle className="w-3.5 h-3.5" />
                    : <AlertTriangle className="w-3.5 h-3.5" />
                  }
                  <span>{cfg.lastTestResult}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSDIntegrationPage;
